import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Title, Paper, Stack, Group, Text, Badge, TextInput, ActionIcon, Tooltip, Menu, Modal, Select, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconSearch, IconUsers, IconPlugConnected, IconDots, IconUsersGroup, IconUserPlus } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { profileTypesApi, groupsApi, groupProfilesApi, usersApi, userProfilesApi } from '../../api/client';
import { ProfileTypeSchema, ProfileAttribute } from '../../hooks/useProfileTypes';
import { PageContainer } from '../../components/PageContainer';
import { PaginatedList } from '../../components/List';
import { Group as GroupType } from '../../types/group';
import { User } from '../../types/user';

function AttributeBadge({ attr }: { attr: ProfileAttribute }) {
  const typeColors: Record<string, string> = {
    string: 'blue',
    number: 'green',
    boolean: 'orange',
    date: 'violet',
    select: 'cyan',
  };

  return (
    <Tooltip label={attr.description || attr.name}>
      <Badge variant="light" color={typeColors[attr.type] || 'gray'} style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
        {attr.label}
        {attr.required && <Text span c="red" style={{ fontSize: 'var(--font-size-xs)' }}> *</Text>}
      </Badge>
    </Tooltip>
  );
}

interface ProfileTypeCardProps {
  schema: ProfileTypeSchema;
  linkedGroups: string[];
  onClick: () => void;
  onLinkGroup: () => void;
  onLinkUser: () => void;
}

function ProfileTypeCard({ schema, linkedGroups, onClick, onLinkGroup, onLinkUser }: ProfileTypeCardProps) {
  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-menu-trigger]') || 
        (e.target as HTMLElement).closest('.mantine-Menu-dropdown')) {
      return;
    }
    onClick();
  };

  return (
    <Paper 
      p="md" 
      withBorder 
      style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
      onClick={handleCardClick}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--mantine-shadow-sm)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} style={{ fontSize: 'var(--font-size-sm)' }}>{schema.name}</Text>
          <Text c="dimmed" truncate style={{ fontSize: 'var(--font-size-xs)' }}>{schema.description || 'Нет описания'}</Text>
          <Group gap="xs">
            <Badge variant="outline" leftSection={<IconPlugConnected size={10} />} style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
              {schema.plugin_id}
            </Badge>
            <Badge variant="light" color="gray" style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
              {schema.attributes.length} атрибутов
            </Badge>
          </Group>
          {linkedGroups.length > 0 && (
            <Group gap={4} wrap="wrap">
              <Text c="dimmed" style={{ fontSize: 'var(--font-size-xs)' }}>Группы:</Text>
              {linkedGroups.map(name => (
                <Badge key={name} variant="light" color="blue" leftSection={<IconUsersGroup size={10} />} style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
                  {name}
                </Badge>
              ))}
            </Group>
          )}
          <Group gap={4} wrap="wrap">
            {schema.attributes.slice(0, 4).map(attr => (
              <AttributeBadge key={attr.name} attr={attr} />
            ))}
            {schema.attributes.length > 4 && (
              <Badge variant="light" color="gray" style={{ padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>
                +{schema.attributes.length - 4}
              </Badge>
            )}
          </Group>
        </Stack>
        <Menu shadow="md" width={220} position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" size="sm" data-menu-trigger onClick={(e) => e.stopPropagation()}>
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconUsers size={14} />} onClick={onClick}>
              Пользователи с профилем
            </Menu.Item>
            <Menu.Divider />
            <Menu.Label>Привязать профиль</Menu.Label>
            <Menu.Item leftSection={<IconUsersGroup size={14} />} onClick={onLinkGroup}>
              Привязать к группе
            </Menu.Item>
            <Menu.Item leftSection={<IconUserPlus size={14} />} onClick={onLinkUser}>
              Привязать к пользователю
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Paper>
  );
}

interface GroupProfileLink {
  group_id: string;
  group_name: string;
  profile_type: string;
}

export function ProfileTypesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [linkGroupModal, setLinkGroupModal] = useState(false);
  const [linkUserModal, setLinkUserModal] = useState(false);
  const [selectedProfileType, setSelectedProfileType] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);

  // Load groups
  const { data: groups = [] } = useQuery<GroupType[]>({
    queryKey: ['groups'],
    queryFn: async () => {
      const res = await groupsApi.list();
      return res.data?.data || [];
    },
  });

  // Load all group-profile links
  const { data: groupProfileLinks = [] } = useQuery<GroupProfileLink[]>({
    queryKey: ['all-group-profiles'],
    queryFn: async () => {
      const allLinks: GroupProfileLink[] = [];
      for (const group of groups) {
        try {
          const res = await groupProfilesApi.getByGroup(group.id);
          const profiles = res.data?.data || [];
          profiles.forEach((p: { profile_type: string }) => {
            allLinks.push({ group_id: group.id, group_name: group.name, profile_type: p.profile_type });
          });
        } catch { /* ignore */ }
      }
      return allLinks;
    },
    enabled: groups.length > 0,
  });

  // Load users for linking
  const { data: users = [] } = useQuery<User[]>({
    queryKey: ['users-for-link'],
    queryFn: async () => {
      const res = await usersApi.list({ limit: 100 });
      return res.data?.data?.data || res.data?.data || [];
    },
    enabled: linkUserModal,
  });

  const handleLinkGroup = (profileType: string) => {
    setSelectedProfileType(profileType);
    setSelectedGroup(null);
    setLinkGroupModal(true);
  };

  const handleLinkUser = (profileType: string) => {
    setSelectedProfileType(profileType);
    setSelectedUser(null);
    setLinkUserModal(true);
  };

  const submitLinkGroup = async () => {
    if (!selectedProfileType || !selectedGroup) return;
    setLinking(true);
    try {
      await groupProfilesApi.link(selectedGroup, selectedProfileType);
      notifications.show({ title: 'Успех', message: 'Профиль привязан к группе', color: 'green' });
      queryClient.invalidateQueries({ queryKey: ['group-profiles'] });
      setLinkGroupModal(false);
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось привязать профиль', color: 'red' });
    } finally {
      setLinking(false);
    }
  };

  const submitLinkUser = async () => {
    if (!selectedProfileType || !selectedUser) return;
    setLinking(true);
    try {
      await userProfilesApi.set(selectedUser, selectedProfileType);
      notifications.show({ title: 'Успех', message: 'Профиль привязан к пользователю', color: 'green' });
      queryClient.invalidateQueries({ queryKey: ['user-profiles'] });
      setLinkUserModal(false);
    } catch {
      notifications.show({ title: 'Ошибка', message: 'Не удалось привязать профиль', color: 'red' });
    } finally {
      setLinking(false);
    }
  };

  return (
    <PageContainer>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group>
            <ActionIcon variant="subtle" onClick={() => navigate(-1)}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <Title order={2}>Типы профилей</Title>
          </Group>
        </Group>

        <TextInput
          placeholder="Поиск по названию или плагину..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        <PaginatedList<ProfileTypeSchema>
          queryKey={['profile-types', { search }]}
          fetchFn={async (offset, limit) => {
            const res = await profileTypesApi.list();
            let data = (res.data?.data || []) as ProfileTypeSchema[];
            if (search) {
              const s = search.toLowerCase();
              data = data.filter(pt =>
                pt.name.toLowerCase().includes(s) ||
                pt.plugin_id.toLowerCase().includes(s) ||
                pt.description?.toLowerCase().includes(s)
              );
            }
            const total = data.length;
            const paged = data.slice(offset, offset + limit);
            return { data: paged, total };
          }}
          renderItem={(schema) => {
            const linkedGroups = groupProfileLinks
              .filter(l => l.profile_type === schema.id)
              .map(l => l.group_name);
            return (
              <ProfileTypeCard
                schema={schema}
                linkedGroups={linkedGroups}
                onClick={() => navigate(`/admin/profiles/${schema.id}/users`)}
                onLinkGroup={() => handleLinkGroup(schema.id)}
                onLinkUser={() => handleLinkUser(schema.id)}
              />
            );
          }}
          getItemKey={(schema) => schema.id}
          emptyMessage="Типы профилей не найдены"
          pageSize={20}
          showPageInfo
          showPageSizeSelector
        />
      </Stack>

      {/* Link to Group Modal */}
      <Modal opened={linkGroupModal} onClose={() => setLinkGroupModal(false)} title="Привязать профиль к группе">
        <Stack gap="md">
          <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>
            Все пользователи выбранной группы получат этот профиль
          </Text>
          <Select
            label="Группа"
            placeholder="Выберите группу"
            data={groups.map(g => ({ value: g.id, label: g.name }))}
            value={selectedGroup}
            onChange={setSelectedGroup}
            searchable
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setLinkGroupModal(false)}>Отмена</Button>
            <Button onClick={submitLinkGroup} loading={linking} disabled={!selectedGroup}>
              Привязать
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Link to User Modal */}
      <Modal opened={linkUserModal} onClose={() => setLinkUserModal(false)} title="Привязать профиль к пользователю">
        <Stack gap="md">
          <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>
            Пользователь получит этот профиль напрямую
          </Text>
          <Select
            label="Пользователь"
            placeholder="Выберите пользователя"
            data={users.map(u => ({ value: u.id, label: `${u.login} ${u.first_name ? `(${u.first_name} ${u.last_name || ''})` : ''}`.trim() }))}
            value={selectedUser}
            onChange={setSelectedUser}
            searchable
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setLinkUserModal(false)}>Отмена</Button>
            <Button onClick={submitLinkUser} loading={linking} disabled={!selectedUser}>
              Привязать
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContainer>
  );
}

export default ProfileTypesPage;
